import { useState, useEffect, useCallback } from 'react';
import { Layout } from '@/components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import axios from 'axios';
import { 
  ClipboardList, 
  Package, 
  Upload, 
  Image, 
  FileIcon,
  Clock,
  Hammer,
  Check,
  RefreshCw,
  Search,
  Printer,
  Eye,
  Edit,
  ChevronRight,
  Calendar,
  User,
  MapPin,
  GripVertical,
  Trash2,
  Layers,
  Plus,
  Warehouse,
  X,
  CreditCard
} from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${API_URL}/api`;

// Configuração das colunas do Kanban
const KANBAN_COLUMNS = {
  aguardando: { 
    label: 'Aguardando', 
    color: 'bg-amber-500', 
    bgColor: 'bg-amber-500/10',
    borderColor: 'border-amber-500/30',
    icon: Clock 
  },
  em_producao: { 
    label: 'Em Produção', 
    color: 'bg-orange-500', 
    bgColor: 'bg-orange-500/10',
    borderColor: 'border-orange-500/30',
    icon: Hammer 
  },
  finalizado: { 
    label: 'Finalizado', 
    color: 'bg-emerald-500', 
    bgColor: 'bg-emerald-500/10',
    borderColor: 'border-emerald-500/30',
    icon: Check 
  }
};

// Mapear status antigos para novos
const mapStatus = (status) => {
  const map = {
    'rascunho': 'aguardando',
    'em_elaboracao': 'aguardando',
    'aguardando_desenho': 'aguardando',
    'aguardando_revisao': 'aguardando',
    'aprovada': 'aguardando',
    'enviada_producao': 'em_producao',
    'na_fila_producao': 'em_producao',
    'em_producao': 'em_producao',
    'parcialmente_pronta': 'em_producao',
    'finalizada': 'finalizado',
    'finalizado': 'finalizado'
  };
  return map[status] || 'aguardando';
};

const PRIORIDADE_CONFIG = {
  baixa: { label: 'Baixa', color: 'bg-slate-400' },
  normal: { label: 'Normal', color: 'bg-blue-400' },
  alta: { label: 'Alta', color: 'bg-orange-500' },
  urgente: { label: 'Urgente', color: 'bg-red-500' }
};

export default function OrdensServico() {
  const [loading, setLoading] = useState(true);
  const [ordens, setOrdens] = useState([]);
  const [stats, setStats] = useState(null);
  const [busca, setBusca] = useState('');
  const [selectedOS, setSelectedOS] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [uploadingItem, setUploadingItem] = useState(null);
  const [draggedOS, setDraggedOS] = useState(null);
  const [touchDragPos, setTouchDragPos] = useState(null);
  const [showMobileMenu, setShowMobileMenu] = useState(null); // Para menu de ações no mobile
  
  // Estados para criar OS para estoque
  const [novaOSModalOpen, setNovaOSModalOpen] = useState(false);
  const [produtos, setProdutos] = useState([]);
  const [buscaProduto, setBuscaProduto] = useState('');
  const [novaOSData, setNovaOSData] = useState({
    itens: [],
    observacoes_os: '',
    prioridade: 'normal',
    prazo: ''
  });
  const [novoProdutoModal, setNovoProdutoModal] = useState(false);
  const [novoProdutoData, setNovoProdutoData] = useState({
    nome: '',
    tipo: 'movel',
    preco_venda: '',
    descricao: '',
    unidade: 'UN'
  });

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };

      const [ordensRes, statsRes] = await Promise.all([
        axios.get(`${API}/ordens-servico`, { headers }),
        axios.get(`${API}/ordens-servico/stats/resumo`, { headers })
      ]);

      setOrdens(ordensRes.data.ordens || []);
      setStats(statsRes.data);
    } catch (error) {
      console.error('Erro ao carregar OS:', error);
      toast.error('Erro ao carregar ordens de serviço');
    } finally {
      setLoading(false);
    }
  }, []);

  // Buscar produtos para a OS de estoque
  const fetchProdutos = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/products?limit=2000`, { 
        headers: { Authorization: `Bearer ${token}` }
      });
      setProdutos(res.data.products || []);
    } catch (error) {
      console.error('Erro ao carregar produtos:', error);
    }
  }, []);

  // Carregar dados iniciais
  useEffect(() => {
    const loadInitialData = async () => {
      await fetchData();
      await fetchProdutos();
    };
    loadInitialData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Drag and Drop
  const handleDragStart = (e, os) => {
    setDraggedOS(os);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDrop = async (e, novoStatus) => {
    e.preventDefault();
    if (!draggedOS) return;

    const statusAtual = mapStatus(draggedOS.status);
    if (statusAtual === novoStatus) {
      setDraggedOS(null);
      return;
    }

    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/ordens-servico/${draggedOS.id}/mover-status?novo_status=${novoStatus}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`OS movida para ${KANBAN_COLUMNS[novoStatus].label}`);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao mover OS');
    }
    setDraggedOS(null);
  };

  // Touch/Mobile Drag and Drop
  const handleTouchStart = (e, os) => {
    const touch = e.touches[0];
    setDraggedOS(os);
    setTouchDragPos({ x: touch.clientX, y: touch.clientY });
  };

  const handleTouchMove = (e) => {
    if (!draggedOS) return;
    const touch = e.touches[0];
    setTouchDragPos({ x: touch.clientX, y: touch.clientY });
  };

  const handleTouchEnd = async (e) => {
    if (!draggedOS || !touchDragPos) {
      setDraggedOS(null);
      setTouchDragPos(null);
      return;
    }

    // Encontrar a coluna onde o toque terminou
    const columns = document.querySelectorAll('[data-kanban-column]');
    let targetColumn = null;
    
    columns.forEach((col) => {
      const rect = col.getBoundingClientRect();
      if (
        touchDragPos.x >= rect.left &&
        touchDragPos.x <= rect.right &&
        touchDragPos.y >= rect.top &&
        touchDragPos.y <= rect.bottom
      ) {
        targetColumn = col.dataset.kanbanColumn;
      }
    });

    if (targetColumn) {
      const statusAtual = mapStatus(draggedOS.status);
      if (statusAtual !== targetColumn) {
        try {
          const token = localStorage.getItem('token');
          await axios.post(`${API}/ordens-servico/${draggedOS.id}/mover-status?novo_status=${targetColumn}`, {}, {
            headers: { Authorization: `Bearer ${token}` }
          });
          toast.success(`OS movida para ${KANBAN_COLUMNS[targetColumn].label}`);
          fetchData();
        } catch (error) {
          toast.error(error.response?.data?.detail || 'Erro ao mover OS');
        }
      }
    }

    setDraggedOS(null);
    setTouchDragPos(null);
  };

  // Mover OS via botões (alternativa mobile)
  const handleMoveOS = async (os, novoStatus) => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/ordens-servico/${os.id}/mover-status?novo_status=${novoStatus}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`OS movida para ${KANBAN_COLUMNS[novoStatus].label}`);
      setShowMobileMenu(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao mover OS');
    }
  };

  // Excluir OS
  const handleDeleteOS = async (os) => {
    const numero = os.numero_os?.toString().padStart(4, '0') || os.id.slice(0, 8);
    
    if (!window.confirm(`Tem certeza que deseja excluir a OS-${numero}?\n\nEsta ação não pode ser desfeita.`)) {
      return;
    }
    
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/ordens-servico/${os.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`OS-${numero} excluída com sucesso`);
      setModalOpen(false);
      setSelectedOS(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao excluir OS');
    }
  };

  // ===== Funções para OS de Estoque =====
  
  // Adicionar produto à OS de estoque
  const handleAddProdutoOS = (produto) => {
    // Verificar se já existe
    if (novaOSData.itens.find(i => i.produto_id === produto.id)) {
      toast.error('Produto já adicionado');
      return;
    }
    
    setNovaOSData(prev => ({
      ...prev,
      itens: [...prev.itens, {
        produto_id: produto.id,
        produto_nome: produto.nome,
        quantidade: 1,
        tipo_madeira: produto.tipo_madeira || '',
        acabamento: produto.acabamento || '',
        observacoes: ''
      }]
    }));
    setBuscaProduto('');
  };

  // Remover produto da OS de estoque
  const handleRemoveProdutoOS = (produtoId) => {
    setNovaOSData(prev => ({
      ...prev,
      itens: prev.itens.filter(i => i.produto_id !== produtoId)
    }));
  };

  // Atualizar quantidade do item
  const handleUpdateItemOS = (produtoId, field, value) => {
    setNovaOSData(prev => ({
      ...prev,
      itens: prev.itens.map(i => 
        i.produto_id === produtoId ? { ...i, [field]: value } : i
      )
    }));
  };

  // Criar novo produto
  const handleCriarProduto = async () => {
    if (!novoProdutoData.nome.trim()) {
      toast.error('Nome do produto é obrigatório');
      return;
    }
    
    try {
      const token = localStorage.getItem('token');
      const precoVenda = novoProdutoData.preco_venda ? parseFloat(novoProdutoData.preco_venda) : 0;
      const res = await axios.post(`${API}/products`, {
        nome: novoProdutoData.nome,
        tipo: novoProdutoData.tipo,
        preco_base: precoVenda,
        preco_venda: precoVenda,
        custo: 0,  // Campo obrigatório
        descricao: novoProdutoData.descricao,
        unidade: novoProdutoData.unidade
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Produto criado com sucesso!');
      
      // Adicionar o produto recém-criado à OS
      handleAddProdutoOS(res.data);
      
      // Atualizar lista de produtos
      fetchProdutos();
      
      // Fechar modal e limpar form
      setNovoProdutoModal(false);
      setNovoProdutoData({
        nome: '',
        tipo: 'movel',
        preco_venda: '',
        descricao: '',
        unidade: 'UN'
      });
    } catch (error) {
      const errorMsg = error.response?.data?.detail;
      if (Array.isArray(errorMsg)) {
        toast.error('Erro de validação: ' + errorMsg.map(e => e.msg).join(', '));
      } else {
        toast.error(errorMsg || 'Erro ao criar produto');
      }
    }
  };

  // Criar OS para estoque
  const handleCriarOSEstoque = async () => {
    if (novaOSData.itens.length === 0) {
      toast.error('Adicione pelo menos um produto à OS');
      return;
    }
    
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/ordens-servico`, {
        pedido_id: null,
        numero_pedido: null,
        cliente_nome: 'ESTOQUE INTERNO',
        cliente_id: null,
        itens: novaOSData.itens,
        observacoes_os: novaOSData.observacoes_os || 'Produção para estoque',
        prioridade: novaOSData.prioridade,
        prazo: novaOSData.prazo || null
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('OS para estoque criada com sucesso!');
      setNovaOSModalOpen(false);
      setNovaOSData({
        itens: [],
        observacoes_os: '',
        prioridade: 'normal',
        prazo: ''
      });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao criar OS');
    }
  };

  // Filtrar produtos pela busca
  const produtosFiltrados = produtos.filter(p => 
    p.nome?.toLowerCase().includes(buscaProduto.toLowerCase()) ||
    p.codigo?.toLowerCase().includes(buscaProduto.toLowerCase())
  ).slice(0, 10);

  const handlePrint = async (osId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/ordens-servico/${osId}/imprimir`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      window.open(url, '_blank');
    } catch (error) {
      toast.error('Erro ao gerar PDF');
    }
  };

  const handleSaveOS = async () => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/ordens-servico/${selectedOS.id}`, selectedOS, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('OS atualizada!');
      setEditMode(false);
      fetchData();
    } catch (error) {
      toast.error('Erro ao salvar OS');
    }
  };

  const handleFileUpload = async (osId, itemIndex, file) => {
    try {
      setUploadingItem(itemIndex);
      const token = localStorage.getItem('token');
      const formData = new FormData();
      formData.append('file', file);

      await axios.post(`${API}/ordens-servico/${osId}/upload-anexo?item_index=${itemIndex}`, formData, {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      
      toast.success('Anexo enviado!');
      
      const res = await axios.get(`${API}/ordens-servico/${osId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSelectedOS(res.data);
      fetchData();
    } catch (error) {
      toast.error('Erro ao enviar anexo');
    } finally {
      setUploadingItem(null);
    }
  };

  const handleFileUploadGeral = async (osId, file) => {
    try {
      setUploadingItem('geral');
      const token = localStorage.getItem('token');
      const formData = new FormData();
      formData.append('file', file);

      await axios.post(`${API}/ordens-servico/${osId}/upload-anexo-geral`, formData, {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      
      toast.success('Desenho/Projeto enviado!');
      
      const res = await axios.get(`${API}/ordens-servico/${osId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSelectedOS(res.data);
      fetchData();
    } catch (error) {
      toast.error('Erro ao enviar arquivo');
    } finally {
      setUploadingItem(null);
    }
  };

  const handleRemoverAnexoGeral = async (osId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/ordens-servico/${osId}/anexo-geral`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Anexo removido!');
      
      const res = await axios.get(`${API}/ordens-servico/${osId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSelectedOS(res.data);
      fetchData();
    } catch (error) {
      toast.error('Erro ao remover anexo');
    }
  };

  const handleUpdateItem = (itemIndex, field, value) => {
    const newItens = [...selectedOS.itens];
    newItens[itemIndex] = { ...newItens[itemIndex], [field]: value };
    setSelectedOS({ ...selectedOS, itens: newItens });
  };

  // Agrupar ordens por status do Kanban
  const ordensAgrupadas = {
    aguardando: [],
    em_producao: [],
    finalizado: []
  };

  ordens.forEach(os => {
    const status = mapStatus(os.status);
    // Filtrar por busca
    if (busca) {
      const searchLower = busca.toLowerCase();
      const match = (
        os.numero_os?.toString().includes(searchLower) ||
        os.cliente_nome?.toLowerCase().includes(searchLower) ||
        os.numero_pedido?.toString().includes(searchLower)
      );
      if (!match) return;
    }
    if (ordensAgrupadas[status]) {
      ordensAgrupadas[status].push(os);
    }
  });

  const getPrioridadeConfig = (prioridade) => PRIORIDADE_CONFIG[prioridade] || PRIORIDADE_CONFIG.normal;

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-96">
          <div className="text-center">
            <div className="w-16 h-16 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
            <p className="text-muted-foreground">Carregando ordens de serviço...</p>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-4">
        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2">
              <ClipboardList className="h-7 w-7 text-blue-500" />
              Ordens de Serviço
            </h1>
            <p className="text-muted-foreground text-sm">Arraste as OS entre as colunas para alterar o status</p>
          </div>
          <div className="flex gap-2">
            <Button onClick={() => setNovaOSModalOpen(true)} className="bg-blue-600 hover:bg-blue-700">
              <Warehouse className="h-4 w-4 mr-2" />
              Nova OS Estoque
            </Button>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Buscar OS..."
                value={busca}
                onChange={(e) => setBusca(e.target.value)}
                className="pl-10 w-[200px]"
              />
            </div>
            <Button variant="outline" onClick={fetchData}>
              <RefreshCw className="h-4 w-4" />
            </Button>
          </div>
        </div>

        {/* Cards Resumo */}
        {stats && (
          <div className="grid grid-cols-3 gap-4">
            <Card className="border-amber-500/30 bg-amber-500/5">
              <CardContent className="pt-4 pb-3">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs text-muted-foreground">Aguardando</p>
                    <p className="text-2xl font-bold text-amber-600">{stats.aguardando || 0}</p>
                  </div>
                  <Clock className="h-6 w-6 text-amber-500/50" />
                </div>
              </CardContent>
            </Card>
            <Card className="border-orange-500/30 bg-orange-500/5">
              <CardContent className="pt-4 pb-3">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs text-muted-foreground">Em Produção</p>
                    <p className="text-2xl font-bold text-orange-600">{stats.em_producao || 0}</p>
                  </div>
                  <Hammer className="h-6 w-6 text-orange-500/50" />
                </div>
              </CardContent>
            </Card>
            <Card className="border-emerald-500/30 bg-emerald-500/5">
              <CardContent className="pt-4 pb-3">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs text-muted-foreground">Finalizados</p>
                    <p className="text-2xl font-bold text-emerald-600">{stats.finalizado || 0}</p>
                  </div>
                  <Check className="h-6 w-6 text-emerald-500/50" />
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Kanban Board */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 min-h-[500px]">
          {Object.entries(KANBAN_COLUMNS).map(([status, config]) => {
            const StatusIcon = config.icon;
            const items = ordensAgrupadas[status] || [];
            
            return (
              <div
                key={status}
                data-kanban-column={status}
                className={`rounded-lg border-2 ${config.borderColor} ${config.bgColor} p-3 ${
                  draggedOS && touchDragPos ? 'transition-colors' : ''
                }`}
                onDragOver={handleDragOver}
                onDrop={(e) => handleDrop(e, status)}
              >
                {/* Header da coluna */}
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <div className={`p-1.5 rounded ${config.color}`}>
                      <StatusIcon className="h-4 w-4 text-white" />
                    </div>
                    <span className="font-semibold">{config.label}</span>
                  </div>
                  <Badge variant="secondary">{items.length}</Badge>
                </div>

                {/* Cards da coluna */}
                <div className="space-y-2 min-h-[400px]">
                  {items.map((os) => {
                    // Verificar se está atrasado
                    const prazo = os.prazo || os.data_entrega_prevista;
                    let isAtrasado = false;
                    if (prazo && os.status !== 'finalizado') {
                      const dataEntrega = new Date(prazo);
                      const hoje = new Date();
                      hoje.setHours(0, 0, 0, 0);
                      dataEntrega.setHours(0, 0, 0, 0);
                      isAtrasado = dataEntrega < hoje;
                    }
                    
                    return (
                    <div
                      key={os.id}
                      draggable
                      onDragStart={(e) => handleDragStart(e, os)}
                      onTouchStart={(e) => handleTouchStart(e, os)}
                      onTouchMove={handleTouchMove}
                      onTouchEnd={handleTouchEnd}
                      className={`bg-card border rounded-lg p-4 cursor-grab active:cursor-grabbing hover:shadow-md transition-all select-none ${
                        isAtrasado ? 'border-red-500 border-2 bg-red-50/50' : ''
                      } ${draggedOS?.id === os.id ? 'opacity-50 scale-95' : ''}`}
                    >
                      <div className="flex items-start justify-between mb-2">
                        <div className="flex items-center gap-2">
                          <GripVertical className="h-4 w-4 text-muted-foreground touch-none" />
                          <span className="font-bold text-base">OS-{os.numero_os?.toString().padStart(4, '0')}</span>
                        </div>
                        <div className="flex items-center gap-1">
                          <Badge className={`${getPrioridadeConfig(os.prioridade).color} text-white text-sm px-2`}>
                            {getPrioridadeConfig(os.prioridade).label}
                          </Badge>
                          {/* Botão de menu mobile */}
                          <button 
                            className="md:hidden p-1 hover:bg-muted rounded"
                            onClick={(e) => {
                              e.stopPropagation();
                              setShowMobileMenu(showMobileMenu === os.id ? null : os.id);
                            }}
                          >
                            <ChevronRight className={`h-4 w-4 transition-transform ${showMobileMenu === os.id ? 'rotate-90' : ''}`} />
                          </button>
                        </div>
                      </div>
                      
                      {/* Menu de ações mobile */}
                      {showMobileMenu === os.id && (
                        <div className="md:hidden mb-3 p-2 bg-muted/50 rounded-lg space-y-1">
                          <p className="text-xs text-muted-foreground mb-2">Mover para:</p>
                          <div className="flex flex-wrap gap-1">
                            {Object.entries(KANBAN_COLUMNS).map(([targetStatus, targetConfig]) => {
                              if (mapStatus(os.status) === targetStatus) return null;
                              return (
                                <button
                                  key={targetStatus}
                                  onClick={() => handleMoveOS(os, targetStatus)}
                                  className={`text-xs px-2 py-1 rounded ${targetConfig.color} text-white`}
                                >
                                  {targetConfig.label}
                                </button>
                              );
                            })}
                          </div>
                        </div>
                      )}
                      
                      <p className="text-base font-medium truncate mb-1">
                        {os.cliente_nome || 'Cliente não informado'}
                      </p>
                      
                      {os.numero_pedido && (
                        <p className="text-sm text-muted-foreground">
                          {os.numero_pedido?.startsWith('PED-') ? os.numero_pedido : `PED-${os.numero_pedido}`}
                        </p>
                      )}
                      
                      {/* Data de Entrega - Sempre visível */}
                      <div className={`text-sm flex items-center gap-1.5 mt-2 px-2 py-1.5 rounded ${
                        (() => {
                          const prazo = os.prazo || os.data_entrega_prevista || os.data_entrega;
                          if (!prazo) return 'bg-gray-100 text-gray-500';
                          
                          const dataEntrega = new Date(prazo);
                          const hoje = new Date();
                          hoje.setHours(0, 0, 0, 0);
                          dataEntrega.setHours(0, 0, 0, 0);
                          
                          const isFinalizado = os.status === 'finalizado';
                          if (isFinalizado) return 'bg-green-500/20 text-green-600';
                          
                          const atrasado = dataEntrega < hoje;
                          const proximoVencer = !atrasado && (dataEntrega - hoje) / (1000 * 60 * 60 * 24) <= 3;
                          
                          if (atrasado) return 'bg-red-500/20 text-red-600 font-bold';
                          if (proximoVencer) return 'bg-amber-500/20 text-amber-600 font-semibold';
                          return 'bg-blue-500/10 text-blue-600';
                        })()
                      }`}>
                        <Calendar className="h-4 w-4" />
                        <span className="font-medium">
                          Entrega: {(() => {
                            const prazo = os.prazo || os.data_entrega_prevista || os.data_entrega;
                            if (!prazo) return 'Não definida';
                            return new Date(prazo).toLocaleDateString('pt-BR');
                          })()}
                        </span>
                        {(() => {
                          const prazo = os.prazo || os.data_entrega_prevista || os.data_entrega;
                          if (!prazo || os.status === 'finalizado') return null;
                          const dataEntrega = new Date(prazo);
                          const hoje = new Date();
                          hoje.setHours(0, 0, 0, 0);
                          dataEntrega.setHours(0, 0, 0, 0);
                          if (dataEntrega < hoje) return <span className="ml-1 text-red-600">ATRASADO</span>;
                          return null;
                        })()}
                      </div>
                      
                      {/* Informações adicionais da OS */}
                      <div className="grid grid-cols-2 gap-x-2 mt-3 text-sm">
                        {/* Quantidade de Itens */}
                        <div className="flex items-center gap-1.5 text-muted-foreground">
                          <Package className="h-4 w-4" />
                          <span>{os.itens?.length || 0} {os.itens?.length === 1 ? 'item' : 'itens'}</span>
                        </div>
                        
                        {/* Total de Peças */}
                        <div className="flex items-center gap-1.5 text-muted-foreground">
                          <Layers className="h-4 w-4" />
                          <span>{os.itens?.reduce((sum, item) => sum + (item.quantidade || 1), 0) || 0} peça(s)</span>
                        </div>
                      </div>
                      
                      {/* Lista resumida de itens (máximo 2) */}
                      {os.itens && os.itens.length > 0 && (
                        <div className="mt-2 space-y-1">
                          {os.itens.slice(0, 2).map((item, idx) => (
                            <p key={idx} className="text-sm text-muted-foreground truncate pl-2 border-l-2 border-muted">
                              {item.quantidade || 1}x {item.produto_nome || 'Produto'}
                            </p>
                          ))}
                          {os.itens.length > 2 && (
                            <p className="text-sm text-blue-500 pl-2 font-medium">
                              +{os.itens.length - 2} mais...
                            </p>
                          )}
                        </div>
                      )}

                      {/* Indicador de anexos */}
                      {(os.anexo_geral || os.itens?.some(item => item.anexos?.length > 0)) ? (
                        <div className="flex items-center gap-1.5 mt-3">
                          <Image className="h-4 w-4 text-green-500" />
                          <span className="text-sm text-green-600 font-medium">Com anexo</span>
                        </div>
                      ) : (
                        <div className="flex items-center gap-1.5 mt-3">
                          <FileIcon className="h-4 w-4 text-amber-500" />
                          <span className="text-sm text-amber-600 font-medium">Sem anexo</span>
                        </div>
                      )}
                      
                      {/* Botões de ação */}
                      <div className="flex justify-between items-center gap-1 mt-3 pt-3 border-t">
                        <Button size="sm" variant="ghost" className="h-8 px-3" onClick={() => { setSelectedOS(os); setModalOpen(true); }} title="Ver detalhes">
                          <Eye className="h-4 w-4" />
                        </Button>
                        
                        {/* Botão de anexar arquivo */}
                        <div className="relative">
                          <input
                            type="file"
                            accept="image/*,.pdf"
                            onChange={(e) => {
                              if (e.target.files?.[0]) {
                                handleFileUploadGeral(os.id, e.target.files[0]);
                              }
                              e.target.value = '';
                            }}
                            className="hidden"
                            id={`file-upload-card-${os.id}`}
                          />
                          <label htmlFor={`file-upload-card-${os.id}`}>
                            <Button 
                              size="sm" 
                              variant="ghost" 
                              className={`h-7 px-2 cursor-pointer ${!os.anexo_geral && !os.itens?.some(item => item.anexos?.length > 0) ? 'text-amber-600 hover:text-amber-700 hover:bg-amber-50' : ''}`}
                              asChild
                              title="Anexar projeto"
                            >
                              <span>
                                <Upload className="h-3 w-3" />
                              </span>
                            </Button>
                          </label>
                        </div>
                        
                        <Button size="sm" variant="ghost" className="h-7 px-2" onClick={() => handlePrint(os.id)} title="Imprimir">
                          <Printer className="h-3 w-3" />
                        </Button>
                        
                        {/* Botão de excluir - só aparece se não está em produção ou finalizado */}
                        {!['em_producao', 'finalizado'].includes(mapStatus(os.status)) && (
                          <Button 
                            size="sm" 
                            variant="ghost" 
                            className="h-7 px-2 text-red-500 hover:text-red-700 hover:bg-red-50" 
                            onClick={(e) => { e.stopPropagation(); handleDeleteOS(os); }}
                            title="Excluir OS"
                          >
                            <Trash2 className="h-3 w-3" />
                          </Button>
                        )}
                      </div>
                    </div>
                  )})}
                  
                  {items.length === 0 && (
                    <div className="text-center py-8 text-muted-foreground text-sm">
                      Nenhuma OS
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        {/* Modal de Detalhes da OS */}
        <Dialog open={modalOpen} onOpenChange={(open) => { setModalOpen(open); if (!open) setEditMode(false); }}>
          <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
            {selectedOS && (
              <>
                <DialogHeader>
                  <DialogTitle className="flex items-center gap-2">
                    <ClipboardList className="h-5 w-5 text-blue-500" />
                    OS-{selectedOS.numero_os?.toString().padStart(4, '0')}
                    <Badge className={`${KANBAN_COLUMNS[mapStatus(selectedOS.status)]?.color} text-white ml-2`}>
                      {KANBAN_COLUMNS[mapStatus(selectedOS.status)]?.label}
                    </Badge>
                  </DialogTitle>
                </DialogHeader>

                <div className="grid grid-cols-2 gap-4 py-3 text-sm border-b">
                  <div className="flex items-center gap-2">
                    <User className="h-4 w-4 text-muted-foreground" />
                    <span className="font-medium">{selectedOS.cliente_nome || '-'}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Package className="h-4 w-4 text-muted-foreground" />
                    <span>{selectedOS.numero_pedido?.startsWith('PED-') ? selectedOS.numero_pedido : `PED-${selectedOS.numero_pedido || '-'}`}</span>
                  </div>
                  <div className="flex items-center gap-2 col-span-2">
                    <Calendar className="h-4 w-4 text-muted-foreground" />
                    <span className="text-muted-foreground">Entrega:</span>
                    <Input
                      type="date"
                      value={selectedOS.prazo ? selectedOS.prazo.split('T')[0] : ''}
                      onChange={async (e) => {
                        const newDate = e.target.value;
                        try {
                          const token = localStorage.getItem('token');
                          await axios.put(`${API}/ordens-servico/${selectedOS.id}`, 
                            { prazo: newDate },
                            { headers: { Authorization: `Bearer ${token}` } }
                          );
                          setSelectedOS({ ...selectedOS, prazo: newDate });
                          fetchData();
                          toast.success('Data de entrega atualizada!');
                        } catch (error) {
                          toast.error('Erro ao atualizar data');
                        }
                      }}
                      className="h-8 w-40"
                    />
                  </div>
                </div>

                <Tabs defaultValue="itens" className="w-full">
                  <TabsList className="grid w-full grid-cols-2">
                    <TabsTrigger value="itens">Itens e Anexos</TabsTrigger>
                    <TabsTrigger value="info">Informações</TabsTrigger>
                  </TabsList>

                  <TabsContent value="itens" className="space-y-3 mt-3">
                    {/* Anexo Geral - Desenho/Projeto da OS */}
                    <Card className="border-2 border-dashed border-blue-300 bg-blue-50/30">
                      <CardHeader className="py-2 px-3">
                        <CardTitle className="text-sm flex items-center gap-2">
                          <FileIcon className="h-4 w-4 text-blue-600" />
                          Desenho / Projeto da OS
                        </CardTitle>
                      </CardHeader>
                      <CardContent className="py-2 px-3">
                        {selectedOS.anexo_geral ? (
                          <div className="flex items-center justify-between p-2 bg-white rounded border">
                            <a 
                              href={`${API_URL}${selectedOS.anexo_geral.url}`} 
                              target="_blank" 
                              rel="noopener noreferrer"
                              className="flex items-center gap-2 text-blue-600 hover:underline"
                            >
                              {selectedOS.anexo_geral.tipo === 'pdf' ? <FileIcon className="h-4 w-4" /> : <Image className="h-4 w-4" />}
                              <span className="text-sm font-medium">{selectedOS.anexo_geral.nome}</span>
                            </a>
                            <Button 
                              variant="ghost" 
                              size="sm" 
                              className="h-7 text-red-500 hover:text-red-700 hover:bg-red-50"
                              onClick={() => handleRemoverAnexoGeral(selectedOS.id)}
                            >
                              <Trash2 className="h-3 w-3" />
                            </Button>
                          </div>
                        ) : (
                          <div className="text-center py-3">
                            <input
                              type="file"
                              accept="image/*,.pdf"
                              onChange={(e) => {
                                if (e.target.files?.[0]) {
                                  handleFileUploadGeral(selectedOS.id, e.target.files[0]);
                                }
                              }}
                              className="hidden"
                              id="file-upload-geral"
                            />
                            <label htmlFor="file-upload-geral">
                              <Button variant="outline" asChild disabled={uploadingItem === 'geral'} className="cursor-pointer">
                                <span>
                                  <Upload className="h-4 w-4 mr-2" />
                                  {uploadingItem === 'geral' ? 'Enviando...' : 'Anexar Desenho/Projeto'}
                                </span>
                              </Button>
                            </label>
                            <p className="text-xs text-muted-foreground mt-2">
                              Anexe o desenho ou projeto para toda a OS (JPG, PNG ou PDF)
                            </p>
                          </div>
                        )}
                      </CardContent>
                    </Card>

                    {/* Lista de Itens */}
                    <div className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                      Itens da OS ({selectedOS.itens?.length || 0})
                    </div>
                    {selectedOS.itens?.map((item, idx) => (
                      <Card key={idx} className="border-l-4 border-l-blue-500">
                        <CardHeader className="py-2 px-3">
                          <CardTitle className="text-sm flex items-center justify-between">
                            <span className="flex items-center gap-2">
                              <Package className="h-4 w-4" />
                              {item.produto_nome}
                            </span>
                            <Badge variant="outline">Qtd: {item.quantidade}</Badge>
                          </CardTitle>
                        </CardHeader>
                        <CardContent className="py-2 px-3 space-y-3">
                          {/* Variações selecionadas */}
                          {item.variacoes_selecionadas && item.variacoes_selecionadas.length > 0 && (
                            <div className="p-2 bg-purple-500/10 rounded-lg border border-purple-500/30">
                              <p className="text-xs font-medium text-purple-700 mb-1">Variações:</p>
                              <div className="flex flex-wrap gap-1">
                                {item.variacoes_selecionadas.map((v, vIdx) => (
                                  <Badge key={vIdx} variant="secondary" className="text-xs bg-purple-100 text-purple-700">
                                    {v.nome}
                                  </Badge>
                                ))}
                              </div>
                            </div>
                          )}
                          
                          {/* Peças de madeira para produção */}
                          {item.pecas_madeira && item.pecas_madeira.length > 0 && (
                            <div className="p-2 bg-amber-500/10 rounded-lg border border-amber-500/30">
                              <p className="text-xs font-medium text-amber-700 mb-1">
                                Peças de Madeira ({item.pecas_madeira.length}):
                              </p>
                              <div className="space-y-1 text-xs">
                                {item.pecas_madeira.map((peca, pIdx) => (
                                  <div key={pIdx} className="flex justify-between items-center">
                                    <span>
                                      {peca.quantidade || 1}x {peca.comprimento}cm × {peca.largura}cm × {peca.espessura}cm
                                      {peca.origem && <span className="text-purple-600 ml-1">({peca.origem.replace('variacao:', '')})</span>}
                                    </span>
                                    {peca.volume_m3 && (
                                      <span className="text-amber-600">{peca.volume_m3.toFixed(4)} m³</span>
                                    )}
                                  </div>
                                ))}
                              </div>
                              {item.volume_m3 && (
                                <div className="mt-2 pt-1 border-t border-amber-500/30 flex justify-between">
                                  <span className="text-amber-700 font-medium">Volume Total:</span>
                                  <span className="text-amber-700 font-bold">{(item.volume_m3 * item.quantidade).toFixed(4)} m³</span>
                                </div>
                              )}
                            </div>
                          )}
                          
                          {editMode ? (
                            <>
                              <div className="grid grid-cols-2 gap-2">
                                <div>
                                  <Label className="text-xs">Madeira</Label>
                                  <Input 
                                    value={item.madeira || ''} 
                                    onChange={(e) => handleUpdateItem(idx, 'madeira', e.target.value)}
                                    className="h-8 text-sm"
                                  />
                                </div>
                                <div>
                                  <Label className="text-xs">Acabamento</Label>
                                  <Input 
                                    value={item.acabamento || ''} 
                                    onChange={(e) => handleUpdateItem(idx, 'acabamento', e.target.value)}
                                    className="h-8 text-sm"
                                  />
                                </div>
                              </div>
                              <div>
                                <Label className="text-xs">Observações Técnicas</Label>
                                <Textarea 
                                  value={item.observacoes_tecnicas || ''} 
                                  onChange={(e) => handleUpdateItem(idx, 'observacoes_tecnicas', e.target.value)}
                                  rows={2}
                                  className="text-sm"
                                />
                              </div>
                            </>
                          ) : (
                            <div className="grid grid-cols-2 gap-2 text-sm">
                              <div><span className="text-muted-foreground">Madeira:</span> {item.madeira || item.tipo_madeira_nome || '-'}</div>
                              <div><span className="text-muted-foreground">Acabamento:</span> {item.acabamento || '-'}</div>
                              {item.observacoes_tecnicas && (
                                <div className="col-span-2"><span className="text-muted-foreground">Obs:</span> {item.observacoes_tecnicas}</div>
                              )}
                            </div>
                          )}
                        </CardContent>
                      </Card>
                    ))}
                  </TabsContent>

                  <TabsContent value="info" className="space-y-3 mt-3">
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <Label className="text-xs">Prioridade</Label>
                        <Badge className={`${getPrioridadeConfig(selectedOS.prioridade).color} text-white mt-1`}>
                          {getPrioridadeConfig(selectedOS.prioridade).label}
                        </Badge>
                      </div>
                      <div>
                        <Label className="text-xs">Prazo</Label>
                        <p className="text-sm">{selectedOS.prazo ? new Date(selectedOS.prazo).toLocaleDateString('pt-BR') : '-'}</p>
                      </div>
                    </div>
                    {selectedOS.observacoes_pedido && (
                      <div>
                        <Label className="text-xs">Observações do Pedido</Label>
                        <p className="text-sm bg-muted/50 p-2 rounded mt-1">{selectedOS.observacoes_pedido}</p>
                      </div>
                    )}
                    <div className="text-xs text-muted-foreground pt-2 border-t">
                      <p>Criada em: {new Date(selectedOS.createdAt).toLocaleString('pt-BR')}</p>
                      {selectedOS.data_inicio_producao && (
                        <p>Iniciada em: {new Date(selectedOS.data_inicio_producao).toLocaleString('pt-BR')}</p>
                      )}
                      {selectedOS.data_finalizacao && (
                        <p>Finalizada em: {new Date(selectedOS.data_finalizacao).toLocaleString('pt-BR')}</p>
                      )}
                    </div>
                  </TabsContent>
                </Tabs>

                <DialogFooter className="flex gap-2 mt-4">
                  {editMode ? (
                    <>
                      <Button variant="outline" onClick={() => setEditMode(false)}>Cancelar</Button>
                      <Button onClick={handleSaveOS}>Salvar</Button>
                    </>
                  ) : (
                    <>
                      {/* Botão de excluir - só aparece se não está em produção ou finalizado */}
                      {!['em_producao', 'finalizado'].includes(mapStatus(selectedOS.status)) && (
                        <Button 
                          variant="outline" 
                          className="text-red-500 border-red-300 hover:bg-red-50"
                          onClick={() => handleDeleteOS(selectedOS)}
                        >
                          <Trash2 className="h-4 w-4 mr-2" />
                          Excluir OS
                        </Button>
                      )}
                      <Button variant="outline" onClick={() => setEditMode(true)}>
                        <Edit className="h-4 w-4 mr-2" />
                        Editar
                      </Button>
                      <Button variant="outline" onClick={() => handlePrint(selectedOS.id)}>
                        <Printer className="h-4 w-4 mr-2" />
                        Imprimir
                      </Button>
                    </>
                  )}
                </DialogFooter>
              </>
            )}
          </DialogContent>
        </Dialog>

        {/* Indicador visual de arraste mobile */}
        {draggedOS && touchDragPos && (
          <div 
            className="fixed pointer-events-none z-50 bg-card border-2 border-primary rounded-lg p-3 shadow-2xl opacity-90"
            style={{
              left: touchDragPos.x - 100,
              top: touchDragPos.y - 30,
              width: '200px'
            }}
          >
            <p className="font-bold text-sm">OS-{draggedOS.numero_os?.toString().padStart(4, '0')}</p>
            <p className="text-xs text-muted-foreground truncate">{draggedOS.cliente_nome}</p>
          </div>
        )}

        {/* Modal Nova OS para Estoque */}
        <Dialog open={novaOSModalOpen} onOpenChange={setNovaOSModalOpen}>
          <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Warehouse className="h-5 w-5 text-blue-500" />
                Nova OS para Estoque
              </DialogTitle>
            </DialogHeader>

            <div className="space-y-4">
              {/* Busca de Produtos */}
              <div className="space-y-2">
                <Label>Adicionar Produto</Label>
                <div className="flex gap-2">
                  <div className="relative flex-1">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      placeholder="Buscar produto pelo nome ou código..."
                      value={buscaProduto}
                      onChange={(e) => setBuscaProduto(e.target.value)}
                      className="pl-10"
                    />
                  </div>
                  <Button variant="outline" onClick={() => setNovoProdutoModal(true)}>
                    <Plus className="h-4 w-4 mr-1" />
                    Criar Produto
                  </Button>
                </div>
                
                {/* Lista de produtos encontrados */}
                {buscaProduto && produtosFiltrados.length > 0 && (
                  <div className="border rounded-lg max-h-40 overflow-y-auto">
                    {produtosFiltrados.map(produto => (
                      <div 
                        key={produto.id}
                        className="px-3 py-2 hover:bg-muted cursor-pointer flex justify-between items-center border-b last:border-b-0"
                        onClick={() => handleAddProdutoOS(produto)}
                      >
                        <div>
                          <p className="font-medium">{produto.nome}</p>
                          <p className="text-xs text-muted-foreground">
                            {produto.codigo || 'Sem código'} • {produto.tipo || 'Produto'}
                          </p>
                        </div>
                        <Plus className="h-4 w-4 text-green-500" />
                      </div>
                    ))}
                  </div>
                )}
                {buscaProduto && produtosFiltrados.length === 0 && (
                  <p className="text-sm text-muted-foreground text-center py-2">
                    Nenhum produto encontrado. <span className="text-blue-500 cursor-pointer" onClick={() => setNovoProdutoModal(true)}>Criar novo?</span>
                  </p>
                )}
              </div>

              {/* Itens adicionados */}
              <div className="space-y-2">
                <Label>Itens da OS ({novaOSData.itens.length})</Label>
                {novaOSData.itens.length === 0 ? (
                  <div className="text-center py-8 border-2 border-dashed rounded-lg text-muted-foreground">
                    <Package className="h-8 w-8 mx-auto mb-2 opacity-50" />
                    <p>Nenhum produto adicionado</p>
                    <p className="text-xs">Busque produtos acima para adicionar</p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {novaOSData.itens.map((item, idx) => (
                      <Card key={item.produto_id} className="p-3">
                        <div className="flex justify-between items-start">
                          <div className="flex-1">
                            <p className="font-medium">{item.produto_nome}</p>
                            <div className="grid grid-cols-3 gap-2 mt-2">
                              <div>
                                <Label className="text-xs">Quantidade</Label>
                                <Input
                                  type="number"
                                  min="1"
                                  value={item.quantidade}
                                  onChange={(e) => handleUpdateItemOS(item.produto_id, 'quantidade', parseInt(e.target.value) || 1)}
                                  className="h-8"
                                />
                              </div>
                              <div>
                                <Label className="text-xs">Madeira</Label>
                                <Input
                                  placeholder="Tipo"
                                  value={item.tipo_madeira || ''}
                                  onChange={(e) => handleUpdateItemOS(item.produto_id, 'tipo_madeira', e.target.value)}
                                  className="h-8"
                                />
                              </div>
                              <div>
                                <Label className="text-xs">Acabamento</Label>
                                <Input
                                  placeholder="Acabamento"
                                  value={item.acabamento || ''}
                                  onChange={(e) => handleUpdateItemOS(item.produto_id, 'acabamento', e.target.value)}
                                  className="h-8"
                                />
                              </div>
                            </div>
                            <div className="mt-2">
                              <Label className="text-xs">Observações do item</Label>
                              <Input
                                placeholder="Dimensões, detalhes específicos..."
                                value={item.observacoes || ''}
                                onChange={(e) => handleUpdateItemOS(item.produto_id, 'observacoes', e.target.value)}
                                className="h-8"
                              />
                            </div>
                          </div>
                          <Button 
                            variant="ghost" 
                            size="sm" 
                            className="text-red-500 hover:text-red-700"
                            onClick={() => handleRemoveProdutoOS(item.produto_id)}
                          >
                            <X className="h-4 w-4" />
                          </Button>
                        </div>
                      </Card>
                    ))}
                  </div>
                )}
              </div>

              {/* Configurações da OS */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Prioridade</Label>
                  <Select 
                    value={novaOSData.prioridade} 
                    onValueChange={(v) => setNovaOSData(prev => ({ ...prev, prioridade: v }))}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="baixa">Baixa</SelectItem>
                      <SelectItem value="normal">Normal</SelectItem>
                      <SelectItem value="alta">Alta</SelectItem>
                      <SelectItem value="urgente">Urgente</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Prazo de Entrega</Label>
                  <Input
                    type="date"
                    value={novaOSData.prazo}
                    onChange={(e) => setNovaOSData(prev => ({ ...prev, prazo: e.target.value }))}
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label>Observações da OS</Label>
                <Textarea
                  placeholder="Observações gerais para a produção..."
                  value={novaOSData.observacoes_os}
                  onChange={(e) => setNovaOSData(prev => ({ ...prev, observacoes_os: e.target.value }))}
                  rows={2}
                />
              </div>
            </div>

            <DialogFooter className="mt-4">
              <Button variant="outline" onClick={() => setNovaOSModalOpen(false)}>
                Cancelar
              </Button>
              <Button 
                onClick={handleCriarOSEstoque}
                disabled={novaOSData.itens.length === 0}
                className="bg-blue-600 hover:bg-blue-700"
              >
                <Warehouse className="h-4 w-4 mr-2" />
                Criar OS para Estoque
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Modal Criar Novo Produto */}
        <Dialog open={novoProdutoModal} onOpenChange={setNovoProdutoModal}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Plus className="h-5 w-5 text-green-500" />
                Criar Novo Produto
              </DialogTitle>
            </DialogHeader>

            <div className="space-y-4">
              <div className="space-y-2">
                <Label>Nome do Produto *</Label>
                <Input
                  placeholder="Ex: Mesa de Jantar 6 lugares"
                  value={novoProdutoData.nome}
                  onChange={(e) => setNovoProdutoData(prev => ({ ...prev, nome: e.target.value }))}
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Tipo</Label>
                  <Select 
                    value={novoProdutoData.tipo} 
                    onValueChange={(v) => setNovoProdutoData(prev => ({ ...prev, tipo: v }))}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="movel">Móvel</SelectItem>
                      <SelectItem value="madeira">Madeira</SelectItem>
                      <SelectItem value="servico">Serviço</SelectItem>
                      <SelectItem value="acessorio">Acessório</SelectItem>
                      <SelectItem value="outro">Outro</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Unidade</Label>
                  <Select 
                    value={novoProdutoData.unidade} 
                    onValueChange={(v) => setNovoProdutoData(prev => ({ ...prev, unidade: v }))}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="UN">Unidade (UN)</SelectItem>
                      <SelectItem value="M">Metro (M)</SelectItem>
                      <SelectItem value="M2">Metro² (M²)</SelectItem>
                      <SelectItem value="M3">Metro³ (M³)</SelectItem>
                      <SelectItem value="KG">Quilo (KG)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="space-y-2">
                <Label>Preço de Venda (R$)</Label>
                <Input
                  type="number"
                  step="0.01"
                  placeholder="0,00"
                  value={novoProdutoData.preco_venda}
                  onChange={(e) => setNovoProdutoData(prev => ({ ...prev, preco_venda: e.target.value }))}
                />
              </div>

              <div className="space-y-2">
                <Label>Descrição</Label>
                <Textarea
                  placeholder="Detalhes do produto..."
                  value={novoProdutoData.descricao}
                  onChange={(e) => setNovoProdutoData(prev => ({ ...prev, descricao: e.target.value }))}
                  rows={2}
                />
              </div>
            </div>

            <DialogFooter className="mt-4">
              <Button variant="outline" onClick={() => setNovoProdutoModal(false)}>
                Cancelar
              </Button>
              <Button onClick={handleCriarProduto} className="bg-green-600 hover:bg-green-700">
                <Plus className="h-4 w-4 mr-2" />
                Criar e Adicionar
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
}
